/*
 * In-app analytics framework
 *
 * The rest of the site only ever calls the functions in this file:
 *
 *   analyticsPageView(path, title)   - a page has been shown
 *   analyticsVideoView(name)         - a visitor has selected/started a video
 *   analyticsEndSession()            - the current visitor has finished (tablet kiosk only)
 *   analyticsTrack(type, data)       - the core function everything above goes through
 *
 * Events are handed to every provider in `analyticsProviders` (see analytics-providers.js), so
 * swapping analytics tools never touches the rest of the site.
 *
 * Normally the analytics tools look after visitors and sessions themselves. The tablet kiosk is one
 * device used by a stream of different visitors, so it sets analyticsCustomSessions and this file then
 * gives each visitor their own client ID + session ID, starting on their first event and ending when
 * the kiosk calls analyticsEndSession() (see tablet-kiosk.js).
 */

// Off: the analytics tools handle sessions. On: we generate them, and providers are told about them.
let analyticsCustomSessions = false;

let analyticsSession = null;    // {clientId, sessionId}, only ever set when analyticsCustomSessions is on

// Core function: everything is reported through here
function analyticsTrack(type, data) {
    if (analyticsCustomSessions) {
        analyticsEnsureSession();
    }

    analyticsDispatch(type, data || {});
}

function analyticsPageView(path, title) {
    path = path || '/';
    analyticsTrack('page_view', {
        path: path,
        title: title || '',
        slug: analyticsSlug(path) || 'home',
    });
}

function analyticsVideoView(name) {
    analyticsTrack('video_view', {
        name: name || '',
        slug: analyticsSlug(name) || 'unknown',
    });
}

function analyticsEnsureSession() {
    if (analyticsSession) {
        return;
    }

    let now = Math.floor(Date.now() / 1000);
    analyticsSession = {
        clientId: Math.floor(Math.random() * 2147483647) + '.' + now,
        sessionId: now,
    };

    analyticsDispatch('session_start', {});
}

// The next tracked event starts a new session with fresh IDs
function analyticsEndSession() {
    if (!analyticsSession) {
        return;
    }

    analyticsDispatch('session_timeout', {});
    analyticsSession = null;
}

// A provider failing (blocked, offline, not set up yet) must never break the kiosk
function analyticsDispatch(type, data) {
    if (typeof analyticsProviders === 'undefined') {
        return;
    }

    analyticsProviders.forEach(function (provider) {
        try {
            if (typeof provider[type] === 'function') {
                provider[type](analyticsSession, data);
            }
        } catch (e) {
            console.warn('Analytics provider failed on ' + type, e);
        }
    });
}

function analyticsSlug(text) {
    return String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
